import { useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import tradeFromScratchStore from "@/store/tradeFromScratchStore";
import { sendRepoTicket } from "@/store/tradeFromScratchStoreACTIONS";
import {
  returnHighlightedKeys,
  useTicketShell,
} from "./helper/useTicketShell";
import RepoCoreFields from "./repo/RepoCoreFields";
import RepoDetailFields from "./repo/RepoDetailFields";
import { useRepoTicket } from "./repo/useRepoTicket";
import TicketStatusLine from "./ticketFields/TicketStatusLine";
import TicketHeader from "./ticketFields/TicketHeader";

// LQC-2955 - the Leg Creation repo ticket: a collateral bond, T+0 start and a term.
// Only offered while tradeCreationLegs is on.

interface IRepoTicketProps {
  ticketId: string;
}

const RepoTicket = ({ ticketId }: IRepoTicketProps) => {
  const { user, getAccessTokenSilently } = useAuth0();
  const shell = useTicketShell(ticketId);
  const { missingEntries, dateIssue } = useRepoTicket(ticketId);
  const ensureTicket = tradeFromScratchStore((state) => state.ensureRepoTicket);
  const resetTicket = tradeFromScratchStore((state) => state.resetRepoTicket);

  useEffect(() => {
    ensureTicket(ticketId);
  }, [ticketId, ensureTicket]);

  const canSend =
    missingEntries.length === 0 && !dateIssue && !shell.isSending;
  const highlightedKeys = returnHighlightedKeys(
    shell.isHoveringSend,
    canSend,
    missingEntries,
  );

  const handleSend = async () => {
    const sent = await sendRepoTicket({ ticketId, user, getAccessTokenSilently });
    if (sent) shell.markSent();
  };

  return (
    <div
      data-testid="RepoTicketContainer"
      id="RepoTicketContainer"
      className="flex h-full w-full flex-col gap-2"
    >
      <TicketHeader
        testIdPrefix="RepoTicket"
        instrument={shell.instrument}
        onInstrumentChange={shell.setInstrument}
        canSend={canSend}
        isSending={shell.isSending}
        isConfirming={shell.isConfirmingSend}
        onSend={handleSend}
        onClear={() => resetTicket(ticketId)}
        onSendHoverChange={shell.setIsHoveringSend}
      />
      <TicketStatusLine
        testIdPrefix="RepoTicket"
        lastSent={shell.lastSent}
        missingFields={missingEntries.map((entry) => entry.label)}
        hasDateIssue={!!dateIssue}
        isBlocking={shell.isHoveringSend && !canSend}
      />
      <RepoCoreFields ticketId={ticketId} highlightedKeys={highlightedKeys} />
      <RepoDetailFields ticketId={ticketId} highlightedKeys={highlightedKeys} />
    </div>
  );
};

export default RepoTicket;
